/**
 * Collects the runtime signals the recovery controller scores.
 *
 * Deliberately an instance rather than module-level state: the PerformanceObserver
 * and the lifecycle bookkeeping have to die with the controller that created
 * them, or a strict-mode remount leaves a second observer running forever.
 */
import store from '@/lib/configs/store-config'
import { queryClient } from '@/provider/query-provider'
import { dashboardLayoutsAtom } from '@/state/dashboard-layouts'
import { pendingOptimisticOrdersAtom, pendingRfqOrdersAtom } from '@/state/order'
import { activeRfqPopupIdsAtom } from '@/state/rfq'

import {
  DATA_STALE_MS,
  HEAP_USAGE_RATIO_LIMIT,
  LAF_SEVERE_COUNT,
  LAF_SEVERE_MS,
  LAG_SAMPLE_CAPACITY,
  levelForScore,
  LIFECYCLE_GAP_MS,
  memoryLimitForDevice,
  QUERY_BUDGET,
  SCORE_BUDGET_OVER,
  SCORE_DATA_STALE,
  SCORE_LAF_SEVERE,
  SCORE_LIFECYCLE_GAP,
  SCORE_MEMORY_OVER,
  SCORE_RESYNC_FAILED,
  SCORE_TRANSPORT_STALE,
  TRANSPORT_STALE_MS,
  WIDGET_BUDGET,
} from './health-policy'
import { getMountedWidgetCount } from './mounted-widget-registry'
import { hasPendingWidgetConfig } from './pending-widget-config-registry'
import { collectActiveReloadBlockerReasons } from './reload-blocker-registry'
import type {
  LagSample,
  LagSignals,
  LifecycleSignals,
  MemorySignals,
  RecoveryReason,
  ResourceSignals,
  RuntimeHealthAssessment,
  RuntimeSignals,
  UserWorkSignals,
  WebSocketHealth,
} from './types'

/** Non-standard, Chromium-only. Used for diagnostics, never as a sole trigger. */
type ChromiumMemory = {
  usedJSHeapSize: number
  jsHeapSizeLimit: number
}

function readChromiumMemory(): ChromiumMemory | null {
  if (typeof performance === 'undefined') return null
  const memory = (performance as Performance & { memory?: ChromiumMemory }).memory
  if (!memory || typeof memory.usedJSHeapSize !== 'number') return null
  return memory
}

function readDeviceMemoryGb(): number | null {
  if (typeof navigator === 'undefined') return null
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  return typeof deviceMemory === 'number' ? deviceMemory : null
}

/**
 * Long Animation Frames where supported, Long Tasks otherwise. LoAF is the
 * better signal — it captures the whole janky frame including style and layout,
 * not just the script — but it is Chromium-only and recent.
 */
function pickLagEntryType(): string | null {
  if (typeof PerformanceObserver === 'undefined') return null
  const supported = PerformanceObserver.supportedEntryTypes
  if (!supported) return null
  if (supported.includes('long-animation-frame')) return 'long-animation-frame'
  if (supported.includes('longtask')) return 'longtask'
  return null
}

export type SignalCollector = {
  /** Note that the page went away, for the execution-gap calculation. */
  markSuspended: () => void
  /** Note a lifecycle return and what caused it. */
  markReturned: (options: { restoredFromBFCache?: boolean }) => void
  startLagSampling: () => void
  stopLagSampling: () => void
  collect: (reasons: RecoveryReason[], lastResyncFailed: boolean) => RuntimeSignals
  dispose: () => void
}

export function createSignalCollector(getSocketHealth: () => WebSocketHealth): SignalCollector {
  const lagEntryType = pickLagEntryType()

  let hiddenAt: number | null = null
  let hiddenDurationMs = 0
  let lastAliveAt = Date.now()
  let executionGapMs = 0
  let wasSuspended = false
  let wasRestoredFromBFCache = false

  let observer: PerformanceObserver | null = null
  let lagSamples: LagSample[] = []
  let disposed = false

  // A heartbeat is the only way to notice the tab stopped executing: if timers
  // were throttled or frozen, the gap since the last tick is the evidence.
  const heartbeatId = setInterval(() => {
    lastAliveAt = Date.now()
  }, 10_000)

  const pushSample = (sample: LagSample) => {
    lagSamples.push(sample)
    if (lagSamples.length > LAG_SAMPLE_CAPACITY) lagSamples.shift()
  }

  return {
    markSuspended() {
      if (hiddenAt === null) hiddenAt = Date.now()
      wasSuspended = true
      lastAliveAt = Date.now()
    },

    markReturned({ restoredFromBFCache = false } = {}) {
      const now = Date.now()
      hiddenDurationMs = hiddenAt === null ? 0 : now - hiddenAt
      // Time unaccounted for by being hidden — i.e. the tab was frozen or
      // discarded rather than merely backgrounded.
      executionGapMs = Math.max(0, now - lastAliveAt - hiddenDurationMs)
      hiddenAt = null
      lastAliveAt = now
      if (restoredFromBFCache) wasRestoredFromBFCache = true
    },

    startLagSampling() {
      if (disposed || lagEntryType === null || observer !== null) return
      lagSamples = []
      try {
        observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            pushSample({ startTime: entry.startTime, duration: entry.duration })
          })
        })
        observer.observe({ type: lagEntryType, buffered: false })
      } catch {
        // Entry type advertised but rejected — treat as unsupported.
        observer = null
      }
    },

    stopLagSampling() {
      observer?.disconnect()
      observer = null
    },

    collect(reasons, lastResyncFailed): RuntimeSignals {
      const severeCount = lagSamples.filter((s) => s.duration >= LAF_SEVERE_MS).length
      const lag: LagSignals = {
        supported: lagEntryType !== null,
        entryType: lagEntryType,
        sampleCount: lagSamples.length,
        totalDurationMs: lagSamples.reduce((sum, s) => sum + s.duration, 0),
        maxDurationMs: lagSamples.reduce((max, s) => Math.max(max, s.duration), 0),
        severeCount,
      }

      const chromiumMemory = readChromiumMemory()
      const memory: MemorySignals = {
        supported: chromiumMemory !== null,
        usedHeapBytes: chromiumMemory?.usedJSHeapSize ?? null,
        heapLimitBytes: chromiumMemory?.jsHeapSizeLimit ?? null,
        heapUsageRatio:
          chromiumMemory && chromiumMemory.jsHeapSizeLimit > 0
            ? chromiumMemory.usedJSHeapSize / chromiumMemory.jsHeapSizeLimit
            : null,
        deviceMemoryGb: readDeviceMemoryGb(),
      }

      const lifecycle: LifecycleSignals = {
        hiddenDurationMs,
        executionGapMs,
        wasRestoredFromBFCache,
        wasDiscarded:
          typeof document !== 'undefined' &&
          (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true,
        wasSuspended,
      }

      return {
        collectedAt: Date.now(),
        reasons,
        socket: getSocketHealth(),
        lifecycle,
        lag,
        memory,
        resources: collectResourceSignals(getSocketHealth),
        userWork: collectUserWorkSignals(),
        lastResyncFailed,
      }
    },

    dispose() {
      disposed = true
      clearInterval(heartbeatId)
      observer?.disconnect()
      observer = null
      lagSamples = []
    },
  }
}

function collectResourceSignals(getSocketHealth: () => WebSocketHealth): ResourceSignals {
  const queries = queryClient.getQueryCache().getAll()
  const layouts = store.get(dashboardLayoutsAtom)

  let configuredWidgetCount = 0
  for (const page of Object.values(layouts)) {
    configuredWidgetCount += page.activeComponents?.length ?? 0
  }

  return {
    totalQueryCount: queries.length,
    inactiveQueryCount: queries.filter((query) => !query.isActive()).length,
    activeSubscriptionCount: getSocketHealth().activeSubscriptionCount,
    configuredWidgetCount,
    mountedWidgetCount: getMountedWidgetCount(),
    tradingViewContainerCount:
      typeof document === 'undefined'
        ? 0
        : document.querySelectorAll('[data-runtime-widget="tradingview"]').length,
  }
}

function collectUserWorkSignals(): UserWorkSignals {
  return {
    pendingOptimisticOrderCount: Object.keys(store.get(pendingOptimisticOrdersAtom)).length,
    pendingRfqOrderCount: Object.keys(store.get(pendingRfqOrdersAtom)).length,
    activeRfqPopupCount: store.get(activeRfqPopupIdsAtom).length,
    mutatingCount: queryClient.isMutating(),
    hasPendingWidgetConfig: hasPendingWidgetConfig(),
    activeReloadBlockerReasons: collectActiveReloadBlockerReasons(),
  }
}

function blockersFromUserWork(userWork: UserWorkSignals): string[] {
  const reloadBlockers: string[] = []
  if (userWork.pendingOptimisticOrderCount > 0) reloadBlockers.push('pending-orders')
  if (userWork.pendingRfqOrderCount > 0) reloadBlockers.push('pending-rfq-orders')
  if (userWork.activeRfqPopupCount > 0) reloadBlockers.push('active-rfq-popup')
  if (userWork.mutatingCount > 0) reloadBlockers.push('in-flight-mutation')
  if (userWork.hasPendingWidgetConfig) reloadBlockers.push('unsaved-widget-config')
  reloadBlockers.push(...userWork.activeReloadBlockerReasons)
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    reloadBlockers.push('not-visible')
  }
  return reloadBlockers
}

/**
 * Freshly re-checks whether a reload is currently safe, without collecting a
 * full RuntimeSignals snapshot. Level 3 calls this right before executing
 * window.location.reload() — the assessment that decided on Level 3 can be a
 * few hundred ms to a couple seconds stale by the time the pre-reload config
 * flush finishes, and that's still enough time for a new order or widget edit
 * to land.
 */
export function getReloadBlockersNow(): string[] {
  return blockersFromUserWork(collectUserWorkSignals())
}

/**
 * Score the signals and recommend a level.
 *
 * Pure, so the whole policy is unit-testable without a browser. The score is
 * guidance for choosing a level, never a verdict on its own — Level 3 in
 * particular also requires lower levels to have failed and safeToReload to hold.
 */
export function assess(signals: RuntimeSignals): RuntimeHealthAssessment {
  const reasons: string[] = []
  let score = 0

  const { socket, lifecycle, lag, memory, resources, userWork } = signals
  const now = signals.collectedAt

  if (!socket.isReady) {
    score += SCORE_TRANSPORT_STALE
    reasons.push('socket-not-ready')
  } else if (
    socket.lastTransportMessageAt !== null &&
    now - socket.lastTransportMessageAt > TRANSPORT_STALE_MS
  ) {
    // Nothing at all — not even a server ping — for well over a ping interval.
    score += SCORE_TRANSPORT_STALE
    reasons.push('transport-stale')
  }

  // Shadow signal only. A quiet market looks identical to a dead data stream
  // through one global timestamp, so this is scored but never on its own
  // sufficient to trigger an automatic restart (see shouldForceLevel1).
  if (
    socket.isReady &&
    socket.lastDataMessageAt !== null &&
    socket.activeSubscriptionCount > 0 &&
    now - socket.lastDataMessageAt > DATA_STALE_MS
  ) {
    score += SCORE_DATA_STALE
    reasons.push('data-stale')
  }

  if (signals.lastResyncFailed) {
    score += SCORE_RESYNC_FAILED
    reasons.push('resync-failed')
  }

  if (lag.supported && lag.severeCount >= LAF_SEVERE_COUNT) {
    score += SCORE_LAF_SEVERE
    reasons.push('ui-lag-severe')
  }

  const heapLimit = memory.supported
    ? memoryLimitForDevice(memory.deviceMemoryGb)
    : HEAP_USAGE_RATIO_LIMIT
  if (memory.heapUsageRatio !== null && memory.heapUsageRatio > heapLimit) {
    score += SCORE_MEMORY_OVER
    reasons.push('heap-over-budget')
  }

  if (resources.totalQueryCount > QUERY_BUDGET || resources.mountedWidgetCount > WIDGET_BUDGET) {
    score += SCORE_BUDGET_OVER
    reasons.push('resource-over-budget')
  }

  if (
    lifecycle.executionGapMs > LIFECYCLE_GAP_MS ||
    lifecycle.wasRestoredFromBFCache ||
    lifecycle.wasDiscarded
  ) {
    score += SCORE_LIFECYCLE_GAP
    reasons.push('lifecycle-gap')
  }

  const reloadBlockers = blockersFromUserWork(userWork)

  return {
    score,
    recommendedLevel: levelForScore(score),
    reasons,
    safeToReload: reloadBlockers.length === 0,
    reloadBlockers,
  }
}

/**
 * Whether the signals justify an automatic Level 1 on their own.
 *
 * Only trustworthy evidence counts here. `data-stale` is excluded on purpose:
 * it cannot be told apart from a quiet market, and acting on it would restart
 * the socket for users who have nothing wrong with them.
 */
export function shouldForceLevel1(signals: RuntimeSignals, longAbsence: boolean): boolean {
  const { socket } = signals
  const now = signals.collectedAt

  if (!socket.isReady) return true
  if (
    socket.lastTransportMessageAt !== null &&
    now - socket.lastTransportMessageAt > TRANSPORT_STALE_MS
  ) {
    return true
  }
  return longAbsence
}
